import React from "react";
import { motion } from "framer-motion";

const tickers = [
  { sym: "EUR/USD", price: "1.08412", chg: "+0.42%", up: true },
  { sym: "GBP/JPY", price: "192.044", chg: "+0.86%", up: true },
  { sym: "XAU/USD", price: "2,341.18", chg: "-0.31%", up: false },
  { sym: "BTC/USD", price: "67,840", chg: "+1.24%", up: true },
  { sym: "USD/JPY", price: "156.71", chg: "-0.12%", up: false },
  { sym: "AUD/USD", price: "0.6612", chg: "+0.18%", up: true },
  { sym: "ETH/USD", price: "3,512", chg: "+0.94%", up: true },
  { sym: "NAS100", price: "18,720", chg: "+0.55%", up: true },
];

const Spark = ({ up = true }) => {
  const path = up
    ? "M0 28 L12 22 L24 25 L36 14 L48 17 L60 8 L72 11 L84 4"
    : "M0 6 L12 11 L24 8 L36 18 L48 14 L60 22 L72 18 L84 28";
  const color = up ? "#4ea8ff" : "#7c8497";
  return (
    <svg viewBox="0 0 84 32" className="h-8 w-[120px]">
      <defs>
        <linearGradient id={`g-${up ? "u" : "d"}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L84 32 L0 32 Z`} fill={`url(#g-${up ? "u" : "d"})`} />
      <path d={path} stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Signal = ({ pair, type, duration, payout, conf }) => (
  <div className="flex items-center justify-between rounded-xl border border-white/[0.05] bg-white/[0.02] px-4 py-3">
    <div className="flex items-center gap-3">
      <div
        className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold tracking-wider ${
          type === "UP"
            ? "bg-[#4ea8ff]/15 text-[#cfe6ff] ring-1 ring-[#4ea8ff]/40 shadow-[0_0_12px_rgba(78,168,255,0.35)]"
            : "bg-white/[0.04] text-white/70 ring-1 ring-white/15"
        }`}
      >
        {type === "UP" ? "▲" : "▼"}
      </div>
      <div>
        <div className="font-display text-[14px] tracking-wide text-white">{pair}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">{type} · OTC</div>
      </div>
    </div>
    <div className="flex items-center gap-5 text-[11px] font-mono-luxe text-white/70">
      <div><span className="text-white/40">Dur </span>{duration}</div>
      <div><span className="text-white/40">Pay </span>{payout}</div>
      <div className="text-[#cfe6ff]">{conf}%</div>
    </div>
  </div>
);

const DashboardPreview = () => {
  return (
    <section className="relative -mt-6 md:-mt-10 pb-32 md:pb-40">
      <div className="relative mx-auto max-w-[1180px] px-6 md:px-10">
        {/* Ambient glow under panel */}
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-10 h-[180px] w-[80%] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(78,168,255,0.28),transparent_70%)] blur-2xl" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
          className="glass-panel relative overflow-hidden rounded-[28px] p-3 md:p-4"
          data-testid="dashboard-preview"
        >
          {/* Top chrome */}
          <div className="flex items-center justify-between px-3 pt-2 pb-3">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
              <span className="ml-4 text-[10px] tracking-[0.3em] uppercase text-white/40">
                BIGBULL · Quotex Terminal
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/45">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4ea8ff] anim-pulse-soft" />
              Live session
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 p-1">
            {/* Chart panel */}
            <div className="lg:col-span-8 glass-soft rounded-2xl p-5 md:p-6 relative overflow-hidden">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                    EUR/USD · OTC · M1
                  </div>
                  <div className="mt-2 flex items-baseline gap-3">
                    <span className="font-display text-[34px] md:text-[40px] tracking-tight text-white">
                      1.08412
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#4ea8ff]/10 px-2 py-0.5 text-[#cfe6ff] text-[11px] font-mono-luxe ring-1 ring-[#4ea8ff]/30">
                      <span>▲</span> UP · 92% Payout
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {["M1", "M5", "M15", "H1"].map((t, i) => (
                    <span
                      key={t}
                      className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase border ${
                        i === 0
                          ? "border-[#4ea8ff]/40 bg-[#4ea8ff]/10 text-white"
                          : "border-white/10 text-white/40"
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Chart svg */}
              <div className="mt-5 h-[220px] md:h-[260px] relative">
                <svg viewBox="0 0 600 240" className="absolute inset-0 h-full w-full">
                  <defs>
                    <linearGradient id="area" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#4ea8ff" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#4ea8ff" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="line" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#cfe6ff" />
                      <stop offset="100%" stopColor="#4ea8ff" />
                    </linearGradient>
                  </defs>
                  {/* grid */}
                  {[0, 1, 2, 3, 4].map((i) => (
                    <line
                      key={i}
                      x1="0"
                      x2="600"
                      y1={40 + i * 40}
                      y2={40 + i * 40}
                      stroke="rgba(255,255,255,0.04)"
                    />
                  ))}
                  <path
                    d="M0 180 C 50 150, 80 170, 120 140 S 200 90, 250 120 S 340 180, 400 130 S 480 60, 540 95 L 600 70 L 600 240 L 0 240 Z"
                    fill="url(#area)"
                  />
                  <path
                    d="M0 180 C 50 150, 80 170, 120 140 S 200 90, 250 120 S 340 180, 400 130 S 480 60, 540 95 L 600 70"
                    stroke="url(#line)"
                    strokeWidth="1.8"
                    fill="none"
                  />
                  {/* signal markers */}
                  <circle cx="250" cy="120" r="4" fill="#4ea8ff" />
                  <circle cx="250" cy="120" r="10" fill="#4ea8ff" opacity="0.18" />
                  <circle cx="540" cy="95" r="4" fill="#4ea8ff" />
                  <circle cx="540" cy="95" r="10" fill="#4ea8ff" opacity="0.18" />
                </svg>
              </div>
            </div>

            {/* Side panel */}
            <div className="lg:col-span-4 flex flex-col gap-3 md:gap-4">
              {/* PnL card */}
              <div className="glass-soft rounded-2xl p-5">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                  Session Performance
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-[32px] text-white">+ 4.82%</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[11px] font-mono-luxe">
                  <div>
                    <div className="text-white/40">Win</div>
                    <div className="text-white mt-1">82%</div>
                  </div>
                  <div>
                    <div className="text-white/40">Trades</div>
                    <div className="text-white mt-1">11</div>
                  </div>
                  <div>
                    <div className="text-white/40">Avg Dur</div>
                    <div className="text-[#cfe6ff] mt-1">M1</div>
                  </div>
                </div>
              </div>

              {/* Signals list */}
              <div className="glass-soft rounded-2xl p-3 md:p-4 flex-1">
                <div className="px-2 pt-1 pb-3 flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                    Live Signals
                  </div>
                  <div className="text-[10px] text-white/30 font-mono-luxe">14:32 UTC</div>
                </div>
                <div className="flex flex-col gap-2">
                  <Signal pair="EUR/USD OTC" type="UP" duration="M1" payout="92%" conf={95} />
                  <Signal pair="GBP/JPY OTC" type="UP" duration="M1" payout="86%" conf={92} />
                  <Signal pair="USD/CHF OTC" type="DOWN" duration="M5" payout="88%" conf={88} />
                </div>
              </div>
            </div>
          </div>

          {/* Ticker strip */}
          <div className="mt-3 md:mt-4 glass-soft rounded-2xl overflow-hidden">
            <div className="marquee-fade-x overflow-hidden py-3">
              <div className="flex w-max anim-ticker">
                {[...tickers, ...tickers].map((t, i) => (
                  <div key={i} className="flex items-center gap-3 px-6 border-r border-white/[0.04]">
                    <span className="font-display text-[12px] tracking-wide text-white/80">{t.sym}</span>
                    <span className="font-mono-luxe text-[12px] text-white/60">{t.price}</span>
                    <span className={`font-mono-luxe text-[11px] ${t.up ? "text-[#4ea8ff]" : "text-white/40"}`}>
                      {t.chg}
                    </span>
                    <Spark up={t.up} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <p className="mt-6 text-center text-[11px] tracking-[0.3em] uppercase text-white/30">
          Interface preview · Quotex binary precision
        </p>
      </div>
    </section>
  );
};

export default DashboardPreview;
